import { Formik, FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { ErrorText } from "@/components/errors";
import { IConsultantProfile, IWorkStyle } from "@/data/consultant-profiles";
import { Flex, Text } from "@/eworkitComponents";
import { FormikCheckbox, FormikForm } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";
import {
  FormikWorkstylesSection,
  validationWorkstylesFormSchema,
} from "@/views/ConsultantProfiles/Detail/WorkStylesSection/WorkstylesSectionEdit";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

export interface IWorkstylesFormSectionValues {
  workStyles?: IWorkStyle[];
  updateWorkstyles?: boolean;
}

interface IWorkstylesFormSectionProps {
  form: FormikProps<IWorkstylesFormSectionValues>;
}

export const WORKSTYLES_MESSENGER_CONTEXT = "profile-workStyles-form";

interface IWorkstylesFormProps extends IWorkstylesFormSectionProps, Partial<IEditableSectionRenderProps> {
  source: IConsultantProfile;
  updateWorkstylesCheckbox?: boolean;
}

export const WorkstylesFormComponent: FC<IWorkstylesFormProps> = memo(
  ({ form, editableProps = null, updateWorkstylesCheckbox = false }) => {
    const { t } = useTranslation("session");

    return (
      <Flex direction="column" innerMargin="md">
        <Messenger context={WORKSTYLES_MESSENGER_CONTEXT} />
        <Flex direction="column">
          <Flex direction="column" innerMargin="md">
            <FormikWorkstylesSection
              name="workStyles"
              testName="workStyles"
              value={form.values.workStyles!}
              setFieldTouched={form.setFieldTouched}
              setAllPointsDistributed={(distributed: boolean) => {
                editableProps?.setSaveDisabled(!distributed);
              }}
            />
            {updateWorkstylesCheckbox && (
              <Flex direction="row" align="center">
                <FormikCheckbox name="updateWorkstyles" checked={form.values.updateWorkstyles} size="lg">
                  <Text>{t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.UPDATE_WORKSTYLES_TEXT")}</Text>
                </FormikCheckbox>
              </Flex>
            )}
            {form.errors.workStyles && form.submitCount > 0 && (
              <ErrorText error={t("CONSULTANT_PROFILES.DETAIL.WORK_STYLES.ERROR")} />
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

export const WorkstylesForm: FC<IWorkstylesFormProps> = memo(props => {
  const { form, editableProps } = props;
  useEditableSubmit(form, editableProps!);

  return (
    <FormikForm>
      <WorkstylesFormComponent {...props} />
    </FormikForm>
  );
});

interface IWorkstylesFormikProps extends IProfileFormikProps<IWorkstylesFormSectionValues>, IWorkstylesFormProps {}

export const WorkstylesFormik: FC<IWorkstylesFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(form: FormikProps<IWorkstylesFormSectionValues>) => <WorkstylesForm {...rest} form={form} />}
    </Formik>
  ),
);

export const WorkstylesEdit = profilePartialUpdaterBuilder(WorkstylesFormik, {
  validationSchema: validationWorkstylesFormSchema,
  messengerContext: WORKSTYLES_MESSENGER_CONTEXT,
});
